import {Component, computed, inject, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {toSignal} from "@angular/core/rxjs-interop"
import {MatButtonModule} from "@angular/material/button"
import {MatDialog} from "@angular/material/dialog"
import {MatTableModule} from "@angular/material/table"
import {MatTooltipModule} from "@angular/material/tooltip"
import {Router, RouterLink} from "@angular/router"
import {JobFarmInstancesTableFragment} from "@app/platform/components/job-farm-instances/job-farm-instances-table/job-farm-instance-table.generated"
import {ItemListComponent} from "@common/components/item/item-list/item-list.component"
import {ListInfoComponent} from "@common/components/item/list-info/list-info.component"
import {InfiniteListComponent} from "@common/components/lists/infinite-list/infinite-list.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {TimeAgoPipe} from "@common/pipes/time-ago/time-ago.pipe"
import {AuthService} from "@common/services/auth/auth.service"
import {ItemUtils} from "@common/services/item-utils/item-utils.service"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {ContentTypeModel, MutationUpdateJobFarmInstanceInput} from "@generated"
import {UtilsService} from "@legacy/helpers/utils"
import {
    EditJobFarmInstanceDialogComponent,
    JobFarmInstanceDialogResult,
} from "@platform/components/job-farm-instances/edit-job-farm-instance-dialog/edit-job-farm-instance-dialog.component"
import {
    JobFarmInstancesDeleteJobFarmInstanceGQL,
    JobFarmInstancesTableItemsGQL,
    JobFarmInstancesUpdateJobFarmInstanceGQL,
} from "@platform/components/job-farm-instances/job-farm-instances-table/job-farm-instance-table.generated"
import {parseOtherInfo} from "@platform/models/job-farm-instance/other-info"
import {firstValueFrom} from "rxjs"

type JobFarmInstanceWithLegacyId = JobFarmInstancesTableFragment & {legacyId: number}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-job-farm-instances-table",
    templateUrl: "./job-farm-instances-table.component.html",
    styleUrls: ["./job-farm-instances-table.component.scss"],
    imports: [MatTableModule, MatButtonModule, RouterLink, MatTooltipModule, TimeAgoPipe, InfiniteListComponent, ListInfoComponent],
})
export class JobFarmInstancesTableComponent extends ItemListComponent<JobFarmInstanceWithLegacyId, MutationUpdateJobFarmInstanceInput> implements OnInit {
    displayedColumns: string[] = [
        "id",
        "hostName",
        "cpuCores",
        "ram",
        "gpu",
        "gpuMemory",
        "group",
        "lastReport",
        "state",
        "type",
        "version",
        "user",
        "claimedTask",
    ]

    readonly $dataSource = computed(() =>
        this.$items()
            .map((item) => item.data)
            .filter(Boolean),
    )

    // Use filter from service
    private readonly $jobFarmInstanceFilter = toSignal(this.filters.jobFarmInstanceFilter$, {initialValue: {}})

    readonly itemsGql = inject(JobFarmInstancesTableItemsGQL)
    readonly deleteJobFarmInstanceGql = inject(JobFarmInstancesDeleteJobFarmInstanceGQL)
    readonly updateJobFarmInstanceGql = inject(JobFarmInstancesUpdateJobFarmInstanceGQL)

    public override router = inject(Router)
    public authService = inject(AuthService)
    public override dialog = inject(MatDialog)
    private notification = inject(NotificationsService)
    public itemUtils = inject(ItemUtils)
    public utils = inject(UtilsService)

    // OVERLOADS
    protected override _contentTypeModel = ContentTypeModel.JobFarmInstance

    protected _fetchList = ({skip, take}: {skip: number; take: number}) =>
        fetchThrowingErrors(this.itemsGql)({
            take: take,
            skip: skip,
            filter: this.$jobFarmInstanceFilter(),
        }).then(({items, totalCount}) => ({
            items: items.filter(Boolean).map((item, index) => ({...item, legacyId: index + 1}) as JobFarmInstanceWithLegacyId),
            totalCount: totalCount,
        }))

    protected _refreshItem = ({id}: {id?: string}) =>
        fetchThrowingErrors(this.itemsGql)({
            take: 1,
            filter: {...this.$jobFarmInstanceFilter(), id: id ? {equals: id} : undefined},
        }).then(({items}) => {
            const item = items.find((item) => item?.id === id)
            return item ? ({...item, legacyId: 0} as JobFarmInstanceWithLegacyId) : undefined
        })

    override ngOnInit() {
        super.ngOnInit()
    }

    private onActionSuccess() {
        this.refresh.all(false)
    }

    private onActionError(err: string) {
        this.notification.showError(err)
        this.refresh.all(false)
    }

    private async showEditDialog(item: JobFarmInstancesTableFragment) {
        const dialogRef = this.dialog.open<EditJobFarmInstanceDialogComponent, JobFarmInstancesTableFragment, JobFarmInstanceDialogResult>(
            EditJobFarmInstanceDialogComponent,
            {
                width: "350px",
                data: {...item},
            },
        )

        const result = await firstValueFrom(dialogRef.afterClosed())
        if (!result) return
        if (result?.delete) {
            await mutateThrowingErrors(this.deleteJobFarmInstanceGql)({id: result.id})
                .then(() => {
                    this.onActionSuccess()
                    this.notification.showInfo("Instance deleted.")
                })
                .catch((error) => this.onActionError(error))
        } else if (result.groupId != item.group?.id) {
            await mutateThrowingErrors(this.updateJobFarmInstanceGql)({
                input: {
                    id: result.id,
                    groupId: result.groupId,
                },
            })
                .then(() => {
                    this.onActionSuccess()
                    this.notification.showInfo("Instance updated.")
                })
                .catch((error) => this.onActionError(error))
        }
    }

    async rowClick(item: JobFarmInstancesTableFragment) {
        return this.showEditDialog(item)
    }

    readonly $parseOtherInfo = signal(parseOtherInfo)
}
